import { useEffect, useState } from "react";
import { API, apiFetch } from "@/utils/api";
import { Button } from "@/components/ui/button";

const AMOUNTS = [
  ["gross_amount", "Bruto"],
  ["income_tax", "IRPF"],
  ["employee_social_security", "Seguridad Social trabajador"],
  ["other_deductions", "Otras deducciones"],
  ["net_amount", "Neto del documento"],
  ["employer_social_security", "Seguridad Social empresa"],
  ["other_employer_costs", "Otros costes empresa"],
];
const WORKER_FIELDS = [
  ["contract_type", "Tipo de contrato"],
  ["working_hours", "Jornada"],
  ["job_title", "Categoría / puesto"],
  ["collective_agreement", "Convenio"],
];
const inputClass = "block w-full mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 min-h-[44px] text-slate-900";
const euros = (value) => value == null ? "Sin dato" : new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(Number(value));
const versionState = (row) => row.status !== "confirmed" ? "Borrador" : row.is_current ? "Vigente" : "Sustituida";

async function request(path, options) {
  const response = await apiFetch(`${API}${path}`, options);
  const data = await response.json();
  if (!response.ok) {
    const message = Array.isArray(data.detail)
      ? data.detail.map((item) => `${item.loc.join(" · ")}: ${item.msg}`).join(". ")
      : data.detail;
    throw new Error(message || "No se ha podido completar la operación.");
  }
  return data;
}

function Field({ label, children }) {
  return <label className="block text-sm font-medium text-slate-700">{label}{children}</label>;
}

export function WorkerForm({ worker, onSaved, onCancel }) {
  const [values, setValues] = useState(() => ({
    name: worker?.name || "", is_active: worker?.is_active ?? true,
    start_date: worker?.start_date || "", end_date: worker?.end_date || "",
    ...Object.fromEntries(WORKER_FIELDS.map(([key]) => [key, worker?.[key] || ""])),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const change = (key, value) => setValues((old) => ({ ...old, [key]: value }));
  const save = async (event) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { ...values, end_date: values.end_date || null };
      WORKER_FIELDS.forEach(([key]) => { payload[key] = values[key].trim() || null; });
      const result = await request(`/payrolls/workers${worker ? `/${worker.worker_id}` : ""}`, {
        method: worker ? "PUT" : "POST", body: JSON.stringify(payload),
      });
      onSaved(result);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="rounded-xl border bg-white p-5 space-y-4" aria-label="Ficha de trabajador">
      <h2 className="text-lg font-semibold">{worker ? "Editar trabajador" : "Nuevo trabajador"}</h2>
      <fieldset disabled={busy} className="grid sm:grid-cols-2 gap-4">
        <Field label="Nombre"><input className={inputClass} required maxLength={200} value={values.name} onChange={(e) => change("name", e.target.value)} /></Field>
        <Field label="Estado del trabajador"><select className={inputClass} value={values.is_active ? "active" : "inactive"} onChange={(e) => change("is_active", e.target.value === "active")}>
          <option value="active">Activo</option><option value="inactive">Inactivo</option>
        </select></Field>
        <Field label="Fecha de inicio"><input className={inputClass} type="date" required value={values.start_date} onChange={(e) => change("start_date", e.target.value)} /></Field>
        <Field label="Fecha de fin (opcional)"><input className={inputClass} type="date" min={values.start_date} value={values.end_date} onChange={(e) => change("end_date", e.target.value)} /></Field>
        {WORKER_FIELDS.map(([key, label]) => <Field key={key} label={`${label} (opcional)`}>
          <input className={inputClass} maxLength={200} value={values[key]} onChange={(e) => change(key, e.target.value)} />
        </Field>)}
      </fieldset>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <div className="flex gap-2"><Button disabled={busy} type="submit">{busy ? "Guardando…" : "Guardar trabajador"}</Button><Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancelar</Button></div>
    </form>
  );
}

export function DraftForm({ draft, workers, onSaved, onCancel }) {
  const [values, setValues] = useState(() => ({
    worker_id: draft?.worker_id || "", accrual_period: draft?.accrual_period || "",
    ...Object.fromEntries(AMOUNTS.map(([key]) => [key, draft?.[key] ?? ""])),
  }));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const change = (key, value) => setValues((old) => ({ ...old, [key]: value }));
  const save = async (event) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { ...values, edit_revision: draft?.edit_revision || 0 };
      AMOUNTS.forEach(([key]) => { payload[key] = values[key] === "" ? null : values[key]; });
      const body = new FormData();
      body.append("data", JSON.stringify(payload));
      if (file) body.append("file", file);
      const result = await request(`/payrolls${draft ? `/${draft.payroll_id}?version_id=${encodeURIComponent(draft.version_id)}` : ""}`, { method: draft ? "PUT" : "POST", body });
      onSaved(result);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="rounded-xl border bg-white p-5 space-y-5" aria-label="Borrador de nómina">
      <h2 className="text-lg font-semibold">{draft ? "Editar borrador" : "Nueva nómina"}</h2>
      <p className="text-sm text-slate-600">Guarda el original y transcribe sus importes. Este borrador no afecta a resultados, contabilidad ni caja.</p>
      {draft?.replaced_version_id && <p className="text-sm">Rectificación: {draft.rectification_reason}. La versión anterior y su PDF se conservan; siguen vigentes hasta confirmar esta rectificación.</p>}
      <fieldset disabled={busy} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Trabajador"><select className={inputClass} required value={values.worker_id} onChange={(e) => change("worker_id", e.target.value)}>
            <option value="">Selecciona una persona</option>
            {workers.map((w) => <option key={w.worker_id} value={w.worker_id}>{w.name}{w.is_active ? "" : " · Inactivo"}</option>)}
          </select></Field>
          <Field label="Periodo de devengo"><input className={inputClass} type="month" required value={values.accrual_period} onChange={(e) => change("accrual_period", e.target.value)} /></Field>
        </div>
        <Field label={draft?.document_id ? "Sustituir PDF original (opcional)" : "PDF original (obligatorio)"}>
          <input className={inputClass} type="file" accept="application/pdf,.pdf" required={!draft?.document_id} onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </Field>
        <p className="text-xs text-slate-500">PDF de hasta 10 MB. Si lo sustituyes, el archivo anterior también se conserva.</p>
        <div>
          <h3 className="font-semibold mb-1">Importes del documento · EUR</h3>
          <p className="text-sm text-slate-600 mb-3">Deja vacío lo que desconozcas. Introduce 0 únicamente si consta que no hay importe.</p>
          <div className="grid sm:grid-cols-2 gap-4">{AMOUNTS.map(([key, label]) => <Field key={key} label={label}>
            <input className={inputClass} type="number" min="0" max="9999999999.99" step="0.01" value={values[key]} onChange={(e) => change(key, e.target.value)} />
          </Field>)}</div>
          <p className="mt-4 text-sm">Coste empresa: se calcula al guardar con bruto + Seguridad Social empresa + otros costes empresa.</p>
        </div>
      </fieldset>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <div className="flex flex-wrap gap-2"><Button disabled={busy} type="submit">{busy ? "Guardando…" : "Guardar borrador y revisar"}</Button><Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancelar</Button></div>
    </form>
  );
}

function OriginalPdf({ document }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let disposed = false;
    let blobUrl;
    const load = async () => {
      try {
        const res = await apiFetch(`${API}/documents/${document.document_id}/download`);
        if (!res.ok) throw new Error("No se ha podido abrir el PDF original.");
        const blob = await res.blob();
        if (!disposed) { blobUrl = URL.createObjectURL(blob); setUrl(blobUrl); }
      } catch (err) { if (!disposed) setError(err.message); }
    };
    load();
    return () => { disposed = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [document.document_id]);
  if (error) return <p role="alert" className="text-red-700">{error}</p>;
  if (!url) return <p role="status">Cargando PDF…</p>;
  return <div className="space-y-3">
    <a className="text-blue-700 underline" href={url} download={document.filename}>Descargar {document.filename}</a>
    <iframe title={`PDF original: ${document.filename}`} src={url} className="w-full h-[32rem] rounded-lg border" />
  </div>;
}

export function RectificationForm({ source, onCreated, onCancel }) {
  const [reason, setReason] = useState("");
  const [reuse, setReuse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const doc = await request(`/payrolls/${source.payroll_id}/rectify`, { method: "POST",
        body: JSON.stringify({ version_id: source.version_id, reason, reuse_pdf: reuse }) });
      onCreated(doc);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return <form aria-label="Crear rectificación" className="rounded-xl border bg-white p-5 space-y-4" onSubmit={create}>
    <h2 className="font-semibold">Rectificar versión {source.version}</h2>
    <p>La versión anterior y su PDF no se borran. Su efecto se mantiene hasta confirmar la nueva versión.</p>
    <Field label="Motivo de rectificación"><textarea className={inputClass} required maxLength={1000} disabled={busy} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    <label className="block"><input type="checkbox" disabled={busy} checked={reuse} onChange={(e) => setReuse(e.target.checked)} /> Reutilizar el mismo PDF: solo estoy corrigiendo la transcripción</label>
    {!reuse && <p className="text-sm">Adjunta el nuevo PDF en el borrador que se abrirá a continuación.</p>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    <Button disabled={busy || !reason.trim()} type="submit">Crear borrador de rectificación</Button>{" "}
    <Button disabled={busy} type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
  </form>;
}

export function DraftReview({ draft, onEdit, onClose, onConfirmed, onRectify, onVersion }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [versions, setVersions] = useState(null);
  const loadHistory = async () => {
    try { const data = await request(`/payrolls/${draft.payroll_id}/versions`); setVersions(data.versions); }
    catch (err) { setError(err.message); }
  };
  const confirmed = draft.status === "confirmed";
  const confirm = async () => {
    setBusy(true); setError("");
    try {
      const result = await request(`/payrolls/${draft.payroll_id}/confirm`, {
        method: "POST", body: JSON.stringify({ version_id: draft.version_id, edit_revision: draft.edit_revision }),
      });
      onConfirmed(result);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const [pdfId, setPdfId] = useState(draft.document_id);
  const review = draft.review;
  const selected = draft.documents?.find((doc) => doc.document_id === pdfId);
  const labels = Object.fromEntries(AMOUNTS);
  return <section className="rounded-xl border bg-white p-5 space-y-5" aria-label="Revisión del borrador">
    <div className="flex flex-wrap justify-between gap-3">
      <div><h2 className="text-lg font-semibold">{draft.worker_name} · {draft.accrual_period}</h2><p className="text-sm text-slate-600">{versionState(draft)} · Versión {draft.version} · Sin conciliar</p></div>
      <div className="flex flex-wrap gap-2">{!confirmed && <Button variant="outline" disabled={busy} onClick={onEdit}>Editar borrador</Button>}{confirmed && draft.is_current && <Button variant="outline" disabled={busy} onClick={onRectify}>Rectificar</Button>}<Button variant="outline" onClick={loadHistory}>Histórico de versiones</Button><Button variant="outline" disabled={busy} onClick={onClose}>Cerrar</Button></div>
    </div>
    <p className="rounded-lg bg-slate-100 p-3 text-sm">{confirmed
      ? draft.is_current ? "Nómina confirmada y contabilizada en su periodo de devengo. Sin conciliar: la confirmación no registra ningún pago bancario." : "Versión sustituida. Se conservan su documento y su asiento original, cuyo efecto está invertido."
      : "Sin efecto financiero mientras sea borrador. Confirma únicamente después de contrastar los importes con el PDF original."}</p>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    <div className="text-sm space-y-1">
      <p>Creación: {draft.created_at || "—"} · Confirmación: {draft.confirmed_at || "—"}</p>
      {draft.rectification_reason && <p>Motivo de rectificación: {draft.rectification_reason}</p>}
      {draft.rectified_at && <p>Rectificación registrada: {draft.rectified_at}</p>}
      {draft.reused_document_from_version_id && <p>PDF reutilizado expresamente para corregir la transcripción.</p>}
    </div>
    {draft.accounting_entry_id && <details className="text-sm"><summary>Referencias contables</summary>
      <p>Asiento: {draft.accounting_entry_id}</p>
      {draft.replaced_entry_id && <p>Asiento original: {draft.replaced_entry_id}</p>}
      {(draft.reversal_entry_id || draft.reversed_by_entry_id) && <p>Inversión: {draft.reversal_entry_id || draft.reversed_by_entry_id}</p>}
    </details>}
    {versions && <div aria-label="Histórico de versiones" className="space-y-2">
      {versions.map((version) => <div key={version.version_id} className="border rounded p-3 text-sm">
        <p>Versión {version.version} · {versionState(version)} · {version.accrual_period} · {euros(version.total_employer_cost)}</p>
        <p>{version.rectification_reason}</p>
        <Button variant="outline" onClick={() => onVersion(version)}>Ver versión {version.version}</Button>
      </div>)}
    </div>}
    {!confirmed && <Button disabled={busy || review.missing_fields.length > 0 || review.net_matches !== true || review.additional_nature_required.length > 0 || !draft.document_id}
      onClick={confirm}>{busy ? "Confirmando…" : "Confirmar nómina"}</Button>}
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <dl className="divide-y">{[...AMOUNTS, ["total_employer_cost", "Coste empresa (calculado)"]].map(([key, label]) => <div key={key} className="flex justify-between gap-3 py-2 text-sm"><dt>{label}</dt><dd className="font-semibold">{euros(draft[key])}</dd></div>)}</dl>
        {review.missing_fields.length > 0 && <p className="text-amber-800">Faltan importes: {review.missing_fields.map((key) => labels[key]).join(", ")}.</p>}
        {review.net_matches === false && <p role="alert" className="text-red-700">El neto del documento no cuadra. Según los importes introducidos sería {euros(review.expected_net)}. No hemos cambiado ninguna cifra.</p>}
        {review.net_matches === true && <p className="text-green-800">El neto coincide con los importes introducidos.</p>}
        {review.additional_nature_required.length > 0 && <p className="text-amber-800">No se puede confirmar todavía: falta una naturaleza contable soportada para {review.additional_nature_required.map((key) => labels[key]).join(", ")}.</p>}
      </div>
      <div className="space-y-3">
        <h3 className="font-semibold">Documentos originales</h3>
        {(draft.documents || []).map((doc) => <button key={doc.document_id} type="button" onClick={() => setPdfId(doc.document_id)} aria-pressed={doc.document_id === pdfId} className="block text-left text-sm text-blue-700 underline min-h-[44px] break-all">
          {doc.filename}{doc.document_id === draft.document_id ? " · Actual" : " · Anterior conservado"}
        </button>)}
        {selected && <OriginalPdf key={selected.document_id} document={selected} />}
      </div>
    </div>
  </section>;
}

export default function Payrolls() {
  const [workers, setWorkers] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workerEditor, setWorkerEditor] = useState(null);
  const [draftEditor, setDraftEditor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rectificationSource, setRectificationSource] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    Promise.all([request("/payrolls/workers"), request(`/payrolls?skip=${skip}&limit=50`)])
      .then(([people, rows]) => {
        if (!cancelled) { setWorkers(people.workers); setPayrolls(rows.payrolls); setTotal(rows.total); }
      }).catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [skip, reload]);
  const show = async (row) => {
    setError("");
    try { const draft = await request(`/payrolls/${row.payroll_id}?version_id=${encodeURIComponent(row.version_id)}`); setSelected(draft); setDraftEditor(null); setRectificationSource(null); }
    catch (err) { setError(err.message); }
  };
  return <div className="space-y-6" data-testid="payrolls-page">
    <div className="flex flex-wrap justify-between gap-3">
      <div><h1 className="text-2xl font-bold">Nóminas</h1><p className="text-slate-600 mt-1">Documentos e importes laborales de tu empresa.</p></div>
      <Button disabled={loading || workers.length === 0} onClick={() => { setDraftEditor({}); setSelected(null); setRectificationSource(null); }}>Nueva nómina</Button>
    </div>
    <details className="rounded-xl border bg-white p-4" open={workers.length === 0 || workerEditor !== null}>
      <summary className="font-semibold cursor-pointer min-h-[44px]">Trabajadores ({workers.length})</summary>
      <div className="space-y-3 mt-2">
        {workers.length === 0 && !loading && <p className="text-sm text-slate-600">Añade una persona para registrar su primera nómina.</p>}
        {workers.map((worker) => <div className="flex justify-between items-center gap-3 border-b py-2" key={worker.worker_id}>
          <span>{worker.name} <span className="text-sm text-slate-500">· {worker.is_active ? "Activo" : "Inactivo"}</span></span>
          <Button variant="outline" onClick={() => setWorkerEditor(worker)}>Editar {worker.name}</Button>
        </div>)}
        <Button variant="outline" onClick={() => setWorkerEditor({})}>Añadir trabajador</Button>
      </div>
    </details>
    {workerEditor && <WorkerForm key={workerEditor.worker_id || "new"} worker={workerEditor.worker_id ? workerEditor : null}
      onSaved={() => { setWorkerEditor(null); setReload((n) => n + 1); }} onCancel={() => setWorkerEditor(null)} />}
    {rectificationSource && <RectificationForm source={rectificationSource}
      onCreated={(doc) => { setRectificationSource(null); setSelected(null); setDraftEditor(doc); setReload((n) => n + 1); }} onCancel={() => setRectificationSource(null)} />}
    {draftEditor && <DraftForm key={`${draftEditor.version_id || "new"}:${draftEditor.edit_revision || 0}`} draft={draftEditor.payroll_id ? draftEditor : null} workers={workers}
      onSaved={(draft) => { setDraftEditor(null); setSelected(draft); setReload((n) => n + 1); }} onCancel={() => setDraftEditor(null)} />}
    {selected && !draftEditor && !rectificationSource && <DraftReview key={`${selected.version_id}:${selected.edit_revision}:${selected.status}`} draft={selected}
      onEdit={() => setDraftEditor(selected)} onClose={() => setSelected(null)}
      onRectify={() => setRectificationSource(selected)} onVersion={show}
      onConfirmed={(doc) => { setSelected(doc); setReload((n) => n + 1); }} />}
    {error && <div role="alert" className="text-red-700">{error} <button className="underline" onClick={() => setReload((n) => n + 1)}>Reintentar carga</button></div>}
    {loading ? <p role="status">Cargando nóminas…</p> : <div className="overflow-x-auto rounded-xl border bg-white">
      <table className="w-full text-sm text-left">
        <thead className="bg-slate-50"><tr>{["Periodo", "Trabajador", "Bruto", "Neto", "Coste empresa", "Estado / conciliación", ""].map((label) => <th key={label} className="p-3 whitespace-nowrap">{label}</th>)}</tr></thead>
        <tbody>{payrolls.map((row) => <tr key={row.version_id} className="border-t">
          <td className="p-3 whitespace-nowrap">{row.accrual_period}</td><td className="p-3">{row.worker_name || "Sin nombre"}</td>
          <td className="p-3 whitespace-nowrap">{euros(row.gross_amount)}</td><td className="p-3 whitespace-nowrap">{euros(row.net_amount)}</td><td className="p-3 whitespace-nowrap">{euros(row.total_employer_cost)}</td>
          <td className="p-3 whitespace-nowrap">{versionState(row)} · Sin conciliar{row.version > 1 ? ` · v${row.version}` : ""}</td><td className="p-3"><Button variant="outline" onClick={() => show(row)}>Revisar</Button></td>
        </tr>)}</tbody>
      </table>
      {payrolls.length === 0 && <p className="p-6 text-slate-600">Todavía no hay nóminas registradas.</p>}
    </div>}
    {total > 50 && <div className="flex items-center gap-3"><Button variant="outline" disabled={skip === 0 || loading} onClick={() => setSkip((n) => n - 50)}>Anterior</Button><span>{skip + 1}–{Math.min(skip + 50, total)} de {total}</span><Button variant="outline" disabled={skip + 50 >= total || loading} onClick={() => setSkip((n) => n + 50)}>Siguiente</Button></div>}
  </div>;
}
